import { useMemo, useState } from 'react'
import { ChevronLeft, ChevronRight, Loader2, CheckCircle } from 'lucide-react'
import { load } from '@cashfreepayments/cashfree-js'
import BookingStepper from './booking/BookingStepper'
import TripStep from './booking/steps/TripStep'
import PersonalInfoStep from './booking/steps/PersonalInfoStep'
import PaymentStep from './booking/steps/PaymentStep'
import PaymentFailedModal from './booking/PaymentFailedModal'
import { getCheckoutMode } from '../../services/checkoutConfig'
import { getCashfreeClientMode } from '../../services/cashfreeConfig'
import { parseGuestCount } from '../../utils/guestCount'
import { isOfflinePayment, isExplicitPaymentFailure, logPaymentAmounts, getPaymentFailureMessage } from '../../utils/paymentPricing'

interface BookingWizardProps {
  roomData: any
  bookingData: {
    checkIn: string
    checkOut: string
    nights: number
    guests: string
    fullName: string
    phone: string
    email: string
    paymentMethod: string
    couponCode: string
  }
  pricing: {
    basePrice: number
    subtotal: number
    discount: number
    total: number
  }
  formErrors: { [key: string]: string }
  paymentMethods: string[]
  isBooking: boolean
  onChange: (name: string, value: string) => void
  onConfirm: () => Promise<{ paymentSessionId?: string | null; bookingNumber?: number; bridgeUrl?: string | null } | void>
  onPaymentComplete: (payload: {
    bookingNumber?: number
    paymentId?: string
    paymentStatus?: string
  }) => Promise<void> | void
  onPaymentFailed: (message: string) => void
  onClose: () => void
}

const STEPS = [
  { id: 'trip', name: 'Trip', description: 'Room, dates, adults' },
  { id: 'details', name: 'You', description: 'Your contact details' },
  { id: 'pay', name: 'Pay', description: 'Payment & confirm' }
]

const BookingWizard = ({
  roomData,
  bookingData,
  pricing,
  formErrors,
  paymentMethods,
  isBooking,
  onChange,
  onConfirm,
  onPaymentComplete,
  onPaymentFailed,
  onClose
}: BookingWizardProps) => {
  const [currentStep, setCurrentStep] = useState(0)
  const [phoneVerified, setPhoneVerified] = useState(false)
  const [emailVerified, setEmailVerified] = useState(true)
  const [showPaymentFailedModal, setShowPaymentFailedModal] = useState(false)
  const [paymentFailedMessage, setPaymentFailedMessage] = useState('')
  const [isRetryingPayment, setIsRetryingPayment] = useState(false)

  const phoneDigits = useMemo(
    () => (bookingData.phone || '').replace(/^\+91/, '').replace(/\D/g, '').slice(0, 10),
    [bookingData.phone]
  )
  const emailValid = useMemo(() => /\S+@\S+\.\S+/.test(bookingData.email || ''), [bookingData.email])
  const phoneFilled = phoneDigits.length === 10
  // `phoneVerified` reflects either an already-verified account number OR a successful in-session OTP verify
  const phoneOk = phoneFilled && phoneVerified

  // Step-by-step validation
  const stepValid: Record<number, boolean> = {
    0: !!(
      bookingData.checkIn &&
      bookingData.checkOut &&
      !formErrors.checkIn &&
      !formErrors.checkOut &&
      parseGuestCount(bookingData.guests) >= 1 &&
      !formErrors.guests
    ),
    1: !!(
      bookingData.fullName?.trim() &&
      emailValid &&
      emailVerified &&
      phoneOk &&
      !formErrors.fullName &&
      !formErrors.phone &&
      !formErrors.email
    ),
    2: !!bookingData.paymentMethod
  }

  const isLastStep = currentStep === STEPS.length - 1
  const canProceed = stepValid[currentStep]

  const handleNext = () => {
    if (canProceed && !isLastStep) setCurrentStep((s) => s + 1)
  }

  const handlePrevious = () => {
    if (currentStep > 0) setCurrentStep((s) => s - 1)
  }

  const readStoredBookingNumber = (): number | undefined => {
    const raw =
      sessionStorage.getItem('cashfree_booking_number') ||
      sessionStorage.getItem('aonlastay_booking_number')
    if (!raw) return undefined
    const n = Number(raw)
    return Number.isFinite(n) && n > 0 ? n : undefined
  }

  const handleConfirm = async () => {
    if (!isLastStep || !canProceed) return
    const isOfflineMethod = isOfflinePayment(bookingData.paymentMethod)

    try {
      const result = await onConfirm()
      if (isOfflineMethod) return

      const bridgeUrl =
        (result && 'bridgeUrl' in result ? result.bridgeUrl : null) ||
        (result && 'bridge_url' in (result as object) ? (result as { bridge_url?: string }).bridge_url : null)

      if (bridgeUrl) {
        sessionStorage.setItem('aonlastay_pending_booking', '1')
        window.location.assign(bridgeUrl)
        return
      }

      const checkoutMode = await getCheckoutMode(bookingData.phone)
      if (checkoutMode === 'partner_redirect') {
        window.alert('Unable to start secure checkout. Please try again in a moment.')
        return
      }

      const paymentSessionId =
        (result && 'paymentSessionId' in result ? result.paymentSessionId : null) ||
        sessionStorage.getItem('cashfree_payment_session_id')
      const bookingNumber =
        (result && 'bookingNumber' in result ? result.bookingNumber : undefined) ||
        readStoredBookingNumber()

      if (!paymentSessionId) {
        window.alert('Unable to start online payment. Please try again.')
        return
      }
      if (!bookingNumber) {
        window.alert('Booking was created but the booking number was not saved. Please contact support.')
        return
      }

      logPaymentAmounts(bookingData.paymentMethod, pricing.total, 'Cashfree checkout')

      await openCashfreePopup(paymentSessionId, bookingNumber)
    } catch {
      // handleBookNow shows field errors / logs failure
    }
  }

  const showPaymentFailed = (message: string) => {
    setPaymentFailedMessage(message)
    setShowPaymentFailedModal(true)
    setCurrentStep(2)
    onPaymentFailed('')
  }

  const handleRetryPayment = async () => {
    const paymentSessionId = sessionStorage.getItem('cashfree_payment_session_id')
    const bookingNumber = readStoredBookingNumber()

    if (!paymentSessionId || !bookingNumber) {
      showPaymentFailed('Payment session expired. Please confirm booking again.')
      setShowPaymentFailedModal(false)
      return
    }

    setIsRetryingPayment(true)
    setShowPaymentFailedModal(false)

    try {
      logPaymentAmounts(bookingData.paymentMethod, pricing.total, 'Cashfree retry')
      await openCashfreePopup(paymentSessionId, bookingNumber)
    } finally {
      setIsRetryingPayment(false)
    }
  }

  const openCashfreePopup = async (sessionIdFromBooking?: string, bookingNumber?: number) => {
    try {
      const cashfreeMode = await getCashfreeClientMode(bookingData.phone)
      const cashfree = await load({ mode: cashfreeMode })
      if (!cashfree) throw new Error('Payment SDK failed to initialize')

      const paymentSessionId =
        sessionIdFromBooking || sessionStorage.getItem('cashfree_payment_session_id')
      if (!paymentSessionId) throw new Error('Payment session id not found')

      const checkoutResult: any = await cashfree.checkout({
        paymentSessionId,
        redirectTarget: '_modal'
      })

      const paymentId =
        checkoutResult?.paymentDetails?.paymentId ||
        checkoutResult?.paymentDetails?.cfPaymentId ||
        checkoutResult?.payment_id ||
        checkoutResult?.cf_payment_id ||
        checkoutResult?.cfPaymentId
      const rawPaymentStatus =
        checkoutResult?.paymentDetails?.paymentStatus ||
        checkoutResult?.paymentDetails?.status ||
        checkoutResult?.payment_status ||
        checkoutResult?.status ||
        ''
      const normalizedStatus = String(rawPaymentStatus).toLowerCase()

      const resolvedBookingNumber = bookingNumber ?? readStoredBookingNumber()
      if (!resolvedBookingNumber) {
        throw new Error('Booking number is missing for payment confirmation.')
      }

      if (isExplicitPaymentFailure(normalizedStatus)) {
        showPaymentFailed(getPaymentFailureMessage(normalizedStatus))
        return
      }

      await onPaymentComplete({
        bookingNumber: resolvedBookingNumber,
        paymentId: paymentId ? String(paymentId) : undefined,
        paymentStatus: normalizedStatus || (paymentId ? 'success' : undefined),
      })
      setShowPaymentFailedModal(false)
    } catch (error) {
      const message = error instanceof Error ? error.message.toLowerCase() : ''
      const dropped =
        message.includes('closed') ||
        message.includes('cancel') ||
        message.includes('dismiss') ||
        message.includes('abort')
      showPaymentFailed(getPaymentFailureMessage(dropped ? 'user_dropped' : 'failed'))
    }
  }

  const renderStepContent = () => {
    switch (currentStep) {
      case 0:
        return (
          <TripStep
            roomData={roomData}
            bookingData={bookingData}
            errors={formErrors}
            onChange={onChange}
          />
        )
      case 1:
        return (
          <PersonalInfoStep
            fullName={bookingData.fullName}
            phone={bookingData.phone}
            email={bookingData.email}
            errors={formErrors}
            onChange={onChange}
            onPhoneVerifiedChange={setPhoneVerified}
            onEmailVerifiedChange={setEmailVerified}
          />
        )
      case 2:
        return (
          <div className="space-y-4">
            <PaymentStep
              paymentMethod={bookingData.paymentMethod}
              paymentMethods={paymentMethods}
              basePrice={pricing.basePrice}
              nights={bookingData.nights}
              roomTotal={pricing.total}
              error={showPaymentFailedModal ? '' : formErrors.paymentMethod}
              onChange={(value) => onChange('paymentMethod', value)}
            />
          </div>
        )
      default:
        return null
    }
  }

  // Hint shown next to disabled Next button so user knows what's missing
  const nextHint = (() => {
    if (currentStep === 0) {
      // if (!bookingData.checkIn || !bookingData.checkOut) return 'Select your check-in & check-out dates'
      if (parseGuestCount(bookingData.guests) < 1) return 'Choose the number of adults'
      return ''
    }
    if (currentStep === 1) {
      if (!bookingData.fullName?.trim()) return 'Enter your full name'
      if (!phoneFilled) return 'Enter a valid 10-digit mobile number'
      if (!emailValid) return 'Enter a valid email address'
      if (!emailVerified) return 'Verify your email with Google to continue'
      if (!phoneOk) return 'Verify your mobile number to continue'
      return ''
    }
    if (currentStep === 2) {
      if (!bookingData.paymentMethod) return 'Choose a payment method'
      return ''
    }
    return ''
  })()

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-end sm:items-center justify-center sm:p-4 overflow-y-auto">
      <div className="bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl w-full sm:max-w-4xl h-[95vh] sm:h-auto sm:max-h-[90vh] overflow-y-auto flex flex-col">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between z-10 flex-shrink-0">
          <div className="absolute top-1.5 left-1/2 -translate-x-1/2 w-10 h-1 bg-gray-300 rounded-full sm:hidden" />
          <div className="mt-1 sm:mt-0">
            <h2 className="text-lg sm:text-2xl font-bold text-gray-900">Book Your Stay</h2>
            <p className="text-xs sm:text-sm text-gray-500">
              Step {currentStep + 1} of {STEPS.length} · {STEPS[currentStep].description}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors p-1"
            aria-label="Close"
          >
            <span className="text-2xl leading-none">×</span>
          </button>
        </div>

        {/* Stepper */}
        <div className="px-3 sm:px-6 py-2 sm:py-3 border-b border-gray-200 flex-shrink-0">
          <BookingStepper steps={STEPS} currentStep={currentStep} />
        </div>

        {/* Content */}
        <div className="px-3 sm:px-3 py-3 sm:py-3 flex-1 overflow-y-auto">{renderStepContent()}</div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-white border-t border-gray-200 px-3 sm:px-6 py-3 sm:py-4 flex flex-col gap-2 flex-shrink-0 safe-area-bottom">
          {nextHint && (
            <div className="text-[11px] sm:text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
              {nextHint}
            </div>
          )}
          <div className="flex items-center justify-between gap-3">
            <button
              onClick={currentStep === 0 ? onClose : handlePrevious}
              className="flex items-center gap-1.5 sm:gap-2 px-3 sm:px-6 py-2.5 sm:py-3 border-2 border-gray-300 text-gray-700 rounded-lg sm:rounded-xl font-semibold text-sm sm:text-base hover:bg-gray-50 transition-colors"
            >
              <ChevronLeft className="h-4 w-4 sm:h-5 sm:w-5" />
              {currentStep === 0 ? 'Cancel' : 'Back'}
            </button>

            {!isLastStep ? (
              <button
                onClick={handleNext}
                disabled={!canProceed}
                className="flex items-center gap-1.5 sm:gap-2 px-5 sm:px-8 py-2.5 sm:py-3 bg-gradient-to-r from-primary-500 to-primary-600 text-white rounded-lg sm:rounded-xl font-semibold text-sm sm:text-base hover:from-primary-600 hover:to-primary-700 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
                <ChevronRight className="h-4 w-4 sm:h-5 sm:w-5" />
              </button>
            ) : (
              <button
                onClick={handleConfirm}
                disabled={isBooking || !canProceed}
                className="flex items-center gap-1.5 sm:gap-2 px-4 sm:px-8 py-2.5 sm:py-3 bg-gradient-to-r from-green-500 to-green-600 text-white rounded-lg sm:rounded-xl font-semibold text-sm sm:text-base hover:from-green-600 hover:to-green-700 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isBooking ? (
                  <>
                    <Loader2 className="h-4 w-4 sm:h-5 sm:w-5 animate-spin" />
                    <span className="hidden sm:inline">Processing...</span>
                    <span className="sm:hidden">Wait...</span>
                  </>
                ) : (
                  <>
                    <span className="hidden sm:inline">Confirm Booking</span>
                    <span className="sm:hidden">Confirm</span>
                    <CheckCircle className="h-4 w-4 sm:h-5 sm:w-5" />
                  </>
                )}
              </button>
            )}
          </div>
        </div>
      </div>

      <PaymentFailedModal
        isOpen={showPaymentFailedModal}
        message={paymentFailedMessage}
        bookingNumber={readStoredBookingNumber()}
        isRetrying={isRetryingPayment}
        onRetry={handleRetryPayment}
        onClose={() => {
          setShowPaymentFailedModal(false)
          setCurrentStep(2)
        }}
      />
    </div>
  )
}

export default BookingWizard
